import './polyfills';
import type { Application } from 'pixi.js';
import { Signal } from 'typed-signals';
import { Log } from './Log';
import { Preload } from './loader/Preload';
import type { Platform, PlatformUser } from './platform/Platform';
import { RendererPlugin, safeAreaDimensions } from './RendererPlugin';
import { soundManager, SoundTrackId } from './SoundManager';
import { LoadingView } from '../views/LoadingView';
import type { LevelTrackingData } from './state/AppStateController';
import { AppStateController } from './state/AppStateController';
import { AppController } from './AppController';
import { DebugPlugin } from './debug/DebugPlugin';
import { getDebugSectionConfig } from './debug/DebugPluginConstants';
import { SplitTestHandler } from './splitTest/SplitTestHandler';
import initTweenMixin from '../libs/tweenMixin/initTweenMixin';
import initPropMixins from '../libs/propsMixin/initPropMixins';
import { waitForMs } from '../utils/async-utils';
import { LeaderboardPoolService } from './leaderboard/LeaderboardPoolService';
import { initializeLocalization } from './localization/initializeLocalization';
import { initPopulate } from '../libs/layouts/initPopulate';
import { getPopulateFactories } from './factories/getPopulateFactories';
import { activeSplitTests } from './splitTest/activeSplitTests';
import { ResourceController } from './resources/ResourceController';
import { handleEntryPointData } from './entryPoints/handleEntryPointData';
import SoftgamesSplitTestService from './splitTest/SoftgamesSplitTestService';
import { dioramaConfigs } from './state/diorama/DioramaConfig';
import { IAPController } from './iap/IAPController';
import { ResourceTracker } from './resources/ResourceTracker';
import { TournamentService } from './tournaments/TournamentService';
import { MultiplatformHandler } from './multiplatform/MultiplatformHandler';
import type { TrackingModule } from './tracking/types';
import type { IAPProductId } from './iap/types';
import type { InventoryItem } from './resources/types';
import { TransactionReason } from './resources/types';
import initWaitMixin from '../libs/waitMixin/initWaitMixin';
import { addLayoutPostProcessors } from '../libs/layouts/layoutPostProcessors/addLayoutPostProcessors';
import { AvoMetaPropertiesGetter } from './tracking/trackingModules/avo/AvoMetaPropertiesGetter';
import { offersConfig } from './offers/offersConfig';
import { AdsPlayer } from './ads/AdsPlayer';
import { getTrackingModule } from './tracking/getTrackingModule';
import { MacroBalancingController } from '../game/level/difficulty/MacroBalancingController';
import { GameSceneController } from '../game/GameSceneController';
import { ObservableValue } from './state/createObservableValue';
import { debugApplyLockedSaveState } from './debug/utils/debugLockState';
import { GameEventsManager } from '../gameEvents/GameEventsManager';
import { GeoLocator } from './utils/GeoLocator';
import { getPlatformRemoteConfig, loadRemoteConfig } from './firebase/getPlatformRemoteConfig';
import { VariableBank } from './utils/VariableBank';
import { LoadingSpinnerView } from '../views/LoadingSpinnerView';
import { LayoutGallery } from './debug/LayoutGallery/LayoutGallery';
import { getAllLayoutConstructors } from '../libs/layouts/runtime/layoutModules';
import type { XPromoAdContext } from './ads/XPromoAdContext';
import type { FallbackControllerContext, FallbackOfferContext } from './ads/FallbackOfferContext';
import { OfferLocation } from './offers/offersTypes';
import { hs929overrideInterstitialConfig } from './splitTest/tests/hs929interstitials';
import { setDefaultBannerHeight } from '../safearea/SafeAreaProvider';

export interface AppConfig {
  isDev: boolean;
  platform: Platform;
  multiplatformConfig: MultiplatformHandler;
}

export class App {
  public readonly log = new Log();

  public readonly preloader = new Preload();

  public readonly platform: Platform;

  public readonly stateController = new AppStateController();

  public readonly renderer = new RendererPlugin();

  public readonly controller = new AppController();

  public readonly tracking: TrackingModule;

  public readonly leaderboardService = new LeaderboardPoolService(this.stateController);

  public readonly resources: ResourceController;

  public readonly tournamentService: TournamentService | null;

  public iap: IAPController | null = null;

  public readonly macroBalancingController = new MacroBalancingController();

  // public readonly featureControllers: {
  //   collectionEvent?: CollectionEventController;
  // } = {};

  public ads: AdsPlayer | null = null;

  public debug: DebugPlugin | null = null;

  public gameEvents: GameEventsManager = new GameEventsManager(this);

  public utils = {
    variableBank: new VariableBank(),
    geoLocator: new GeoLocator({
      tier1CountryCodes: ['US', 'CA', 'GB', 'DE', 'FR', 'CH', 'NL', 'SE', 'NO', 'DK', 'FI', 'AU', 'NZ', 'JP', 'KR'],
    }),
  };

  public debugUtils = {
    layoutGallery: new LayoutGallery(getAllLayoutConstructors()),
  };

  constructor({ isDev = false, platform, multiplatformConfig }: AppConfig) {
    this.platform = platform;

    this.resources = new ResourceController(this.stateController);

    this.tracking = getTrackingModule(this.platform, this.stateController);
    this.platform.setTrackingModule(this.tracking);
    const resourceTracker = new ResourceTracker({
      tracking: this.tracking,
      getInventory: (): Record<InventoryItem, number> => this.resources.getInventory(),
      getLevelTrackingData: (): LevelTrackingData => this.stateController.getLevelTrackingData(),
      platformTracking: (eventName: string, amount: number): void => {
        this.platform.logEvent(eventName, amount);
      },
      getSceneName: (): string => this.controller.sceneManager?.activeScene?.id ?? 'NONE',
    });
    this.resources.onTransactionMade.connect(resourceTracker.trackTransaction.bind(resourceTracker));

    const { tournaments, leaderboards } = this.platform;

    this.tournamentService =
      tournaments && leaderboards
        ? new TournamentService({
            state: this.stateController,
            leaderboards,
            tournaments,
            globalTournamentUrl: this.platform.platformRemoteConfig.getRemoteConfig().feature.tournament.configUrl,
          })
        : null;

    void this.init(isDev, multiplatformConfig);

    this.renderer.safeArea = this.platform.deviceSafeArea;

    void loadRemoteConfig(this);
  }

  private async init(isDev: boolean, multiplatformConfig: MultiplatformHandler): Promise<void> {
    initPropMixins(this.renderer.pixi, this.preloader);

    this.preloader.onLoadProgressUpdate.connect((progress) => {
      this.platform.setLoadingProgress(progress);
    });

    await this.initPlatform();

    this.setVariables();
    void this.initLeaderboardService();

    await this.preloader.init(this.platform);

    await this.renderer.init();

    initTweenMixin(this.renderer.pixi.ticker);
    initWaitMixin();

    LoadingView.setDependencies({
      getParent: () => this.controller.overlay.view.content,
    });

    if (isDev) debugApplyLockedSaveState();
    await this.stateController.init(this.platform, isDev);

    // Set user segmentation properties for A/B testing (after state is loaded)
    void this.initUserSegmentation();

    void this.utils.geoLocator.init(
      {
        observable: new ObservableValue(this.stateController, ['geoLocation']),
      },
      this.remoteConfigIsTier1.bind(this),
    );
    this.utils.variableBank.setDisplayObjectVariable('loadingSpinner', () => new LoadingSpinnerView());

    this.tracking.addMetaPropertiesGetter(new AvoMetaPropertiesGetter(this));

    this.addHorizontalOrientationListener();

    await this.initSplitTests(isDev, multiplatformConfig);

    const { payments } = this.platform;

    /* eslint-disable @typescript-eslint/naming-convention */

    this.iap = payments
      ? new IAPController({
          app: this,
          offers: offersConfig,
          offersUrl: null,
          controller: this.controller,
          state: this.stateController,
          payments,
          resources: this.resources,
          tracking: this.tracking,
          isDev,
        })
      : null;

    this.macroBalancingController.init({
      isDev,
      onRevive: GameSceneController.onRevive,
      observable: new ObservableValue(this.stateController, ['macroBalancing']),
      onIAPPurchase: this.iap?.onPurchaseSuccessful ?? new Signal(),
      onLevelFailed: GameSceneController.onLevelLost,
      onLevelWin: GameSceneController.onLevelWon,
    });

    this.stateController.postSplitTestInit();

    void this.tournamentService?.init(this.platform.getCurrentPlayer());

    await this.initDioramasPreload();

    this.initPopulate();

    initializeLocalization(this.stateController, this.platform.getSupportedLanguages?.());

    this.debugUtils.layoutGallery.checkForAutomaticOpening();

    const entryPointPayload = this.platform.getEntryPointData();

    if (entryPointPayload) handleEntryPointData(this.platform, this, entryPointPayload);
    this.controller.init(this);

    this.gameEvents.init();

    if (multiplatformConfig.areAdsAvailable()) {
      // this.initialiseAds(multiplatformConfig.getPlatformTrackingConfig(this.platform)?.gameSlug ?? 'hexa-stack');
      this.initialiseAds();
    }

    this.controller.setFirstScene();

    await this.platform.startGame();

    this.initializePayments();

    void this.trackGameInitialized();

    void this.initSounds();

    if (isDev || this.platform.cheatEnabled()) {
      this.initDebugMenu();
      this.debug?.setSafeArea(this.platform.deviceSafeArea?.getSafeAreaInset());
    }
    this.platform.onCheatMenuOptionsChanged.connect((enabled) => {
      void this.stateController.setData({
        cheatEnabled: enabled,
      });
    });

    if (this.platform.forceUpdate && this.platform.forceUpdate.isSupported()) {
      void this.platform.forceUpdate.init();
    }

    if (this.platform.rateUs && this.platform.rateUs.isSupported()) {
      void this.platform.rateUs.init();
    }
  }

  private buildFallbackOfferContext(): FallbackOfferContext {
    return {
      getCatalog: () => this.iap?.getCatalog(OfferLocation.AdFallback, null) ?? null,
    };
  }

  private buildFallbackControllerContext(): FallbackControllerContext {
    return {
      fallbackOfferContext: this.buildFallbackOfferContext(),
      iap: this.iap,
      variableBank: this.utils.variableBank,
    };
  }

  private buildXPromoAdContext(): XPromoAdContext {
    return {
      getPlayerId: () => this.platform.getCurrentPlayer().id,
      onGameSwitch: (payload) => this.platform.switchGame?.(payload) ?? Promise.resolve(),
      supportsSwitchGame: () => this.platform.switchGame !== undefined,
      isPayer: () => this.iap?.isPayer() ?? false,
      getPlatformType: () => this.platform.getPlatformType(),
    };
  }

  private initialiseAds(): void {
    const fallbackControllerContext = this.buildFallbackControllerContext();
    // this.platform.adModule = createPlatformAdModule(fallbackControllerContext, PLATFORM ?? '', gameId, this.buildXPromoAdContext());
    this.platform.adModule = this.platform.createAdsModule(fallbackControllerContext, this.buildXPromoAdContext());

    this.ads = new AdsPlayer({
      getAdModule: () => this.platform.adModule,
      isPayer: () => this.iap?.isPayer() ?? false,
      getLevelNumber: () => this.stateController.getLevelNumber(),
      getLastPurchaseTimestamp: (): number | null => {
        const { iap } = this;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call -- IAPController is typed; circular dep can make eslint infer any
        return iap !== null ? iap.getLastPurchaseTimestamp() : null;
      },
      isTier1: () => this.utils.geoLocator.isTier1(),
      observable: new ObservableValue(this.stateController, ['lastInterstitialAdTime']),
      disableAutomaticInterstitials: () => this.platform.disableAutomaticInterstitials?.() ?? false,
    });

    this.platform.adModule.onAdFinished?.connect((properties) => {
      this.tracking.trackAdFinished(properties);
    });

    // Load interstitial config from remote config asynchronously
    void this.loadInterstitialConfig();
    setDefaultBannerHeight(this.platform.getBannerHeight());
  }

  private async remoteConfigIsTier1(): Promise<boolean> {
    await this.platform.platformRemoteConfig.init();
    const {
      segment: { isTier1 },
    } = this.platform.platformRemoteConfig.getRemoteConfig();
    return isTier1;
  }

  public initDebugMenu(): void {
    if (this.debug) return;
    this.debug = new DebugPlugin(this, getDebugSectionConfig(this));
    this.debug.init();

    if (this.platform.adModule) {
      const adFolder = this.debug.addFolder({ title: 'Ads', expanded: false });
      this.platform.adModule.addDebugOptions(adFolder);
    }

    // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/naming-convention
    (globalThis as unknown as { __PIXI_APP__: Application }).__PIXI_APP__ = this.renderer.pixi;
    (window as unknown as { app: App }).app = this;
  }

  private initializePayments(): void {
    void this.iap?.initialize();

    MultiplatformHandler.setGiftCallback((productId: string) => {
      const product = this.iap?.getProduct(productId as IAPProductId);
      if (!product) return;
      this.resources.addInAppPurchase(product.rewards, TransactionReason.ShopPurchase);
      this.iap?.onPurchaseSuccessful.emit(product.id);
    });
  }

  private async trackGameInitialized(): Promise<void> {
    this.tracking.trackGameInitialized(this.stateController.exportState(), {
      userId: this.platform.getCurrentPlayer().id,
      entryPointName: await this.platform.getEntryPointName(),
      entryPointData: this.platform.getEntryPointData(),
    });
  }

  /**
   * Track native app launch event
   * Call this early in the app lifecycle, after platform initialization
   */
  public trackNativeAppLaunched(properties: {
    appBuildNumber: number;
    appVersion: string;
    memoryTotal?: number;
    memoryAvailable?: number;
    assetQuality?: 'low' | 'high';
    deviceIdentifier: string;
  }): void {
    this.tracking.trackNativeAppLaunched(properties);
  }

  /**
   * Track player attribution data
   * Call this when attribution data is available from mobile attribution providers
   */
  public trackPlayerAttribution(properties: {
    attributionPayload: string;
    attributionNetwork?: string;
    attributionCampaign?: string;
  }): void {
    this.tracking.trackPlayerAttribution(properties);
  }

  private async initPlatform(): Promise<void> {
    await this.platform.init();
  }

  /**
   * Load interstitial config from remote config and update AdsPlayer
   * This is called asynchronously to not block game initialization
   */
  private async loadInterstitialConfig(): Promise<void> {
    try {
      await this.platform.platformRemoteConfig.init();
      const config = this.platform.platformRemoteConfig.getRemoteConfig();
      this.ads?.setInterstitialConfig(hs929overrideInterstitialConfig(config));
    } catch (e) {
      this.ads?.setInterstitialConfig(hs929overrideInterstitialConfig(getPlatformRemoteConfig()));
      console.warn('[App] Failed to load interstitial config, using defaults:', e);
    }
  }

  /**
   * Initialize user segmentation properties for A/B testing
   * Sets Firebase user properties based on session count and country
   */
  private async initUserSegmentation(): Promise<void> {
    try {
      await this.platform.setUserSegmentation(this.stateController.getSessionCount());
    } catch (e) {
      console.warn('[App] Failed to set user segmentation:', e);
    }
  }

  private async initDioramasPreload(): Promise<void> {
    let lastDioramaIndex = this.stateController.getCurrentDioramaState().dioramaIndex;

    this.stateController.subscriptions.onChange('diorama', (state) => {
      const dioramaIndex = state.diorama?.dioramaIndex;
      if (!dioramaIndex) return;
      if (dioramaIndex === lastDioramaIndex) return;

      lastDioramaIndex = dioramaIndex;

      const dioramaConfig = dioramaConfigs[dioramaIndex + 1];
      if (!dioramaConfig) return;
      void this.preloader.loadBundles([dioramaConfig.atlas]);
    });

    await this.preloader.loadBundles([dioramaConfigs[lastDioramaIndex].atlas]);
    const nextDiorama = dioramaConfigs[lastDioramaIndex + 1];
    if (nextDiorama) {
      void this.preloader.loadBundles([nextDiorama.atlas]);
    }
  }

  private async initSounds(): Promise<void> {
    const { stateController: state } = this;
    await this.preloader.loadBundles(['sounds']);

    soundManager.init();
    soundManager.setPlatform(this.platform);
    soundManager.setVolume(state.getSfxVolume(), SoundTrackId.Sfx);
    soundManager.setVolume(state.getMusicVolume(), SoundTrackId.Music);
  }

  private async initSplitTests(isDev: boolean, multiplatformConfig: MultiplatformHandler): Promise<void> {
    const config = multiplatformConfig.getSplitTestConfig();
    if (!config) return;

    if (isDev) {
      SoftgamesSplitTestService.loadDebugOverride();
    }

    const splitTestHandler = new SplitTestHandler();
    const updatedSplitTests = await splitTestHandler.init({
      gameSlug: config.gameSlug,
      playerId: this.platform.getCurrentPlayer().id ?? 'unknown',
      sessionNumber: this.stateController.getSessionCount(),
      activeSplitTests: this.stateController.getActiveSplitTests(),
      supportedTests: activeSplitTests,
      app: this,
    });
    this.stateController.setActiveSplitTests(updatedSplitTests);
    Object.entries(updatedSplitTests).forEach(([slug, distribution]) => {
      this.tracking.splitTest(slug, distribution);
    });
  }

  private async initLeaderboardService(retry = 1): Promise<void> {
    let friends: PlatformUser[] = [];
    try {
      friends = await this.platform.getFriends();
    } catch (e) {
      await waitForMs(retry * 500);
      return this.initLeaderboardService(retry + 1);
    }

    this.leaderboardService.init({
      currentUser: this.platform.getCurrentPlayer(),
      realUsers: friends,
    });

    return Promise.resolve();
  }

  private initPopulate(): void {
    initPopulate({
      runTimeFactories: getPopulateFactories(this),
      getScreenSize: () => this.renderer.appSize,
    });
    addLayoutPostProcessors(this);
  }

  private setVariables(): void {
    this.utils.variableBank.setTextVariable('gameVersion', APP_VERSION);
    this.utils.variableBank.setTextVariable('gameVersionToDisplay', VERSION_TO_DISPLAY);
    this.utils.variableBank.setTextVariable('playerId', this.platform.getCurrentPlayer().id ?? 'unknown');
  }

  private addHorizontalOrientationListener(): void {
    this.renderer.onResize.connect((width) => {
      this.stateController.setHorizontalLayout(width >= safeAreaDimensions.minHorizontalWidth);
    });
    this.stateController.setHorizontalLayout(this.renderer.width >= safeAreaDimensions.minHorizontalWidth);
  }
}
